import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import React, { ComponentProps, useCallback, useContext, useEffect, useRef, useState } from 'react'
import OfferDatePicker from './OfferDatePicker'
import OfferDatePickerSelectBox from './OfferDatePickerSelectBox'
import DropdownSheetBase from 'LuxKit/Dropdown/Sheet/DropdownSheetBase'
import DropdownSheetBody from 'LuxKit/Dropdown/Sheet/DropdownSheetBody'
import DropdownSheetFooter from 'LuxKit/Dropdown/Sheet/DropdownSheetFooter'
import DropdownSheetHeader from 'LuxKit/Dropdown/Sheet/DropdownSheetHeader'
import Sheet from 'components/utils/Sheet'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import Group from 'components/utils/Group'
import { AccommodationMobileDayCellSelectHandler } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarSmallDayCell'
import moment from 'moment'

import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineExclamationTriangleIcon from 'LuxKit/Icons/line/LineExclamationTriangleIcon'
import styled from 'styled-components'
import { rem } from 'polished'

const DatePickerContainer = styled(Sheet)`
  width: min(${rem(800)}, calc(100dvw - ${rem(32)}));
  max-height: ${rem(492)};
`

interface Props extends Pick<ComponentProps<typeof DropdownSheet>, 'open' | 'boundaryRef'> {
  checkInDate?: string;
  checkOutDate?: string;
}

function OfferDatePickerSelect(props: Props) {
  const { open, boundaryRef, checkInDate, checkOutDate } = props

  const [attemptedSubmit, setAttemptedSubmit] = useState(false)
  // store dates locally until we 'commit' them. This is done for performance reasons
  const [dates, setDates] = useState<{ checkIn?: string, checkOut?: string }>({
    checkIn: checkInDate,
    checkOut: checkOutDate,
  })

  useEffect(() => {
    setDates({ checkIn: checkInDate, checkOut: checkOutDate })
  }, [checkInDate, checkOutDate])

  const dispatch = useOfferPageDispatch()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  // const [selectedMonth, setSelectedMonth] = useState<string | undefined>(checkInDate)

  const onCloseCross = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseCross)
    dispatch({ type: OfferPageActions.closeFilter })
    if (!(dates.checkIn && !dates.checkOut)) {
      // only update state if it's not an 'invalid' (has check in, but not check out) selection
      dispatch({ type: OfferPageActions.setDates, checkIn: dates.checkIn, checkOut: dates.checkOut })
    }
  }, [dates.checkIn, dates.checkOut, dispatch, onUserAnalyticsAction])

  const isInvalid = !!dates.checkIn && !dates.checkOut

  const onApply = useCallback<React.MouseEventHandler<HTMLButtonElement>>(() => {
    setAttemptedSubmit(true)
    if (!isInvalid) {
      onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.Apply)
      dispatch({ type: OfferPageActions.setDates, checkIn: dates.checkIn, checkOut: dates.checkOut })
      dispatch({ type: OfferPageActions.closeFilter })
    }
  }, [dates.checkIn, dates.checkOut, dispatch, isInvalid, onUserAnalyticsAction])

  const onReset = useCallback(() => {
    setDates({ checkIn: undefined, checkOut: undefined })
  }, [])

  const triggerRef = useRef<HTMLButtonElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) {
      // Each time we close dropdown, reset that the user attempted to submit it
      // we don't want to show the error message as soon as they open it again
      setAttemptedSubmit(false)
    }
  }, [open])

  // temporarily disabled until we have updated designs for the month view when empty
  // const onShowMonth = useCallback(() => setSelectedMonth(undefined), [])
  // const onSelectMonth = useCallback<AccommodationCalendarYearSelectHandler>((month) => {
  //   setSelectedMonth(formatIsoDate(new Date(month.year, month.month, 1)))
  // }, [])

  const onDatesChange = useCallback<AccommodationMobileDayCellSelectHandler>(day => {
    if (dates.checkIn && !dates.checkOut && moment(day.date).isAfter(dates.checkIn)) {
      setDates({ checkIn: dates.checkIn, checkOut: day.date })
    } else {
      setDates({ checkIn: day.date, checkOut: undefined })
    }
  }, [dates.checkIn, dates.checkOut])

  return <div>
    <OfferDatePickerSelectBox
      ref={triggerRef}
      controlled={open}
      checkIn={dates.checkIn}
      checkOut={dates.checkOut}
    />
    <DropdownSheetBase
      size="content"
      anchorRef={triggerRef}
      triggerRef={triggerRef}
      boundaryRef={boundaryRef}
      onClose={onCloseCross}
      open={open}
      data-testid="offer-checkin-select"
    >
      <DropdownSheetHeader
        title="Select check-in date"
        size="M"
        alignment="start"
        onDismissClick={onCloseCross}
      />
      <DropdownSheetBody ref={bodyRef}>
        <DatePickerContainer padding="24 0">
          <OfferDatePicker
            scrollerRef={bodyRef}
            size="large"
            // onBackToMonth={onShowMonth}
            // onMonthSelect={onSelectMonth}
            // selectedMonth={selectedMonth}
            variant="day-only"
            checkInDate={dates.checkIn}
            checkOutDate={dates.checkOut}
            onDatesChange={onDatesChange}
          />
        </DatePickerContainer>
      </DropdownSheetBody>
      <DropdownSheetFooter
        startSlot={<>
          {isInvalid && attemptedSubmit && <BodyTextBlock
            variant="medium"
            colour="urgency-dark"
            startIcon={<LineExclamationTriangleIcon />}
          >
            Select a checkout date
          </BodyTextBlock>}
          {dates.checkIn && dates.checkOut && <BodyText variant="medium">
            <Group direction="horizontal" verticalAlign="center">
              <span><FormatDateTime value={dates.checkIn} format="short-day-name-day-month" /></span>
              <LineArrowRightIcon size="XS" />
              <span><FormatDateTime value={dates.checkOut} format="short-day-name-day-month" /></span>
            </Group>
          </BodyText>}
        </>}
        secondaryActionProps={dates.checkIn ? { children: 'Reset dates', onClick: onReset, kind: 'tertiary', size: 'large' } : undefined}
        primaryActionProps={{
          'data-testid': 'OfferDatePickerSelect-apply',
          size: 'large',
          children: 'Apply',
          onClick: onApply,
        }}
      />
    </DropdownSheetBase>
  </div>
}

type MappedProps = Pick<Props, 'open' | 'checkInDate' | 'checkOutDate'>

export default withOfferPageState((state): MappedProps => ({
  open: state.activeFilter === 'checkin',
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
}), OfferDatePickerSelect)
